import { useState, useMemo, useEffect } from "react";
import { useParams } from "wouter";
import { formatWeight } from "@shared/weight";
import { useQuery } from "@tanstack/react-query";
import { useStripe, Elements, PaymentElement, ExpressCheckoutElement, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripeClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ListingPhotoPlaceholder } from "@/components/ListingPhotoPlaceholder";
import { FoundingMakerBadge } from "@/components/FoundingMakerBadge";
import { HeatBadge } from "@/components/HeatBadge";
import { FoodRiskAck, foodRiskLabel } from "@/components/FoodRiskAck";
import { WeightSizePicker, type ResolvedWeightUnit } from "@/components/WeightSizePicker";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { calculateFees, calculateExpressFees, cartBulkDiscountTotal, type CartDiscountLine, EXPRESS_MIN_ORDER, EXPRESS_DELIVERY_MIN } from "@shared/fees";
import { trackEvent, trackCartAdd } from "@/lib/analytics";
import { getCartSessionId, rotateCartSessionId } from "@/lib/cartSession";
import { MadeToOrderFields, madeToOrderResponsesComplete } from "@/components/MadeToOrderFields";
import type { MadeToOrderField, CustomResponse } from "@shared/schema";
import {
  Loader2,
  Minus,
  Plus,
  ShoppingBag,
  MapPin,
  ArrowLeft,
  CheckCircle2,
  Store,
  Sparkles,
  X,
  Info,
  Sun,
  Moon,
} from "lucide-react";

if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  throw new Error("Missing required Stripe key: VITE_STRIPE_PUBLIC_KEY");
}

type StorefrontSeller = {
  id: string;
  handle: string;
  displayName: string;
  bio: string | null;
  profileImageUrl: string | null;
  coverImageUrl: string | null;
  sellerType: string | null;
  isFoundingMaker: boolean;
  city: string | null;
  state: string | null;
  chargesEnabled: boolean;
};

type StorefrontItem = {
  id: string;
  name: string;
  description: string | null;
  price: string;
  imageUrl: string | null;
  isActive: boolean;
  status: string | null;
  isMadeToOrder: number | boolean | null;
  unit: string | null;
  heatLevel: number | null;
  category: string | null;
};

type StorefrontResponse = {
  seller: StorefrontSeller;
  items: StorefrontItem[];
};

type IntentResponse = {
  clientSecret: string;
  orderId: string;
  sellerName: string;
  subtotal: number;
  buyerFee: number;
  deliveryFee?: number;
  total: number;
};

type DeliveryQuote = {
  available: boolean;
  free?: boolean;
  estimatedMiles?: number;
  reason?: string;
};

function PaymentForm({
  handle,
  orderId,
  total,
  onSuccess,
}: {
  handle: string;
  orderId: string;
  total: number;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [complete, setComplete] = useState(false);
  const [walletDebug, setWalletDebug] = useState<string | null>(null);
  const showWalletDebug =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("debugwallets") === "1";

  // Apple Pay and Google Pay are NOT accordion rows inside the Payment Element —
  // that element lists card, bank and redirect methods (Cash App, Amazon Pay).
  // The wallets only surface through the Express Checkout Element, so with the
  // Payment Element alone they never appeared no matter how the Stripe Dashboard
  // was configured. Same confirm call as the card form; the element renders
  // nothing at all when the device offers no wallet, so there's no empty slot.
  const confirmWith = async (onFail: () => void) => {
    if (!stripe || !elements) return;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/buy/${handle}?paid=1&orderId=${orderId}`,
      },
      redirect: "if_required",
    });
    if (error) {
      toast({
        title: "Payment failed",
        description: error.message,
        variant: "destructive",
      });
      onFail();
      return;
    }
    onSuccess();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    if (!complete) {
      toast({
        title: "Add your card",
        description: "Enter your card details above before paying.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    await confirmWith(() => setIsProcessing(false));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <ExpressCheckoutElement
        options={{ buttonHeight: 48 }}
        // Stripe reports exactly which wallets it will offer. A missing button
        // looks identical whether the device has no card, the domain isn't
        // verified, or the account has the method off — this is the only way to
        // tell them apart. Console for a tethered Mac, and on screen when the
        // URL carries ?debugwallets=1, so it can be read on the phone itself.
        onReady={(event) => {
          const available = JSON.stringify(event.availablePaymentMethods ?? null);
          console.log("[stoop] express checkout wallets:", available);
          setWalletDebug(available);
        }}
        onConfirm={async () => {
          setIsProcessing(true);
          await confirmWith(() => setIsProcessing(false));
        }}
      />
      {showWalletDebug && (
        <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-[11px] leading-snug">
          wallets Stripe offers: {walletDebug ?? "(express element never became ready)"}
        </pre>
      )}
      <PaymentElement onChange={(event) => setComplete(event.complete)} />
      <Button
        type="submit"
        size="lg"
        className="w-full rounded-full"
        disabled={!stripe || isProcessing}
        data-testid="button-express-pay"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay $${total.toFixed(2)}`
        )}
      </Button>
    </form>
  );
}

export default function SellerStorefront() {
  const stripePromise = useMemo(() => getStripe(), []);
  const { handle = "" } = useParams<{ handle: string }>();
  const { toast } = useToast();

  const [cart, setCart] = useState<Map<string, number>>(new Map());
  // Answers to a listing's required questions (cupcake flavour, etc). Express
  // can now sell made-to-order items, but only once these are filled in.
  const [customAnswers, setCustomAnswers] = useState<Map<string, CustomResponse[]>>(new Map());
  // Sold-by-weight lines, keyed by the reserved unit id — each unit is a
  // unique physical piece with its own exact weight and price.
  const [weightCart, setWeightCart] = useState<
    Map<string, { itemId: string; weight: number; priceCents: number }>
  >(new Map());
  const [sizePickerItem, setSizePickerItem] = useState<StorefrontItem | null>(null);
  const [view, setView] = useState<"browse" | "checkout">("browse");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [orderId, setOrderId] = useState("");
  const [intentTotals, setIntentTotals] = useState<{
    subtotal: number;
    buyerFee: number;
    deliveryFee: number;
    total: number;
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [foodRiskAck, setFoodRiskAck] = useState(false);
  const [fulfillment, setFulfillment] = useState<"pickup" | "delivery">("pickup");
  const [deliveryAddr, setDeliveryAddr] = useState({ line1: "", city: "", state: "", zip: "" });
  const [deliveryQuote, setDeliveryQuote] = useState<DeliveryQuote | null>(null);
  const [quoting, setQuoting] = useState(false);

  // The Stripe return_url redirects back here with ?paid=1; read it on mount
  // so both the inline (redirect: "if_required") and redirected flows land
  // on the confirmation state.
  const initialParams =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [paid, setPaid] = useState(initialParams.get("paid") === "1");
  const [paidOrderId] = useState(initialParams.get("orderId") || "");
  const [showAppCard, setShowAppCard] = useState(true);
  const [showInstallBar, setShowInstallBar] = useState(true);
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const [storefrontDark, setStorefrontDark] = useState(false);
  const [detailItem, setDetailItem] = useState<StorefrontItem | null>(null);

  // The storefront defaults to light (friendlier for a cold guest) regardless
  // of the visitor's system theme, with a toggle. Scoped to this page so it
  // doesn't override the app-wide preference.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(storefrontDark ? "dark" : "light");
  }, [storefrontDark]);

  const { data, isLoading, isError } = useQuery<StorefrontResponse>({
    queryKey: [`/api/storefront/${handle}`],
    queryFn: async () => {
      const res = await fetch(`/api/storefront/${encodeURIComponent(handle)}`);
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: handle.length > 0,
  });

  const seller = data?.seller;
  const items = useMemo(
    () => (data?.items ?? []).filter((i) => i.isActive && i.status !== "draft"),
    [data],
  );

  const itemsById = useMemo(() => {
    const map = new Map<string, StorefrontItem>();
    for (const item of items) map.set(item.id, item);
    return map;
  }, [items]);

  const { itemCount, subtotal } = useMemo(() => {
    let count = 0;
    let sum = 0;
    cart.forEach((qty, itemId) => {
      const item = itemsById.get(itemId);
      if (!item) return;
      count += qty;
      sum += parseFloat(item.price) * qty;
    });
    weightCart.forEach((line) => {
      count += 1;
      sum += line.priceCents / 100;
    });
    return { itemCount: count, subtotal: sum };
  }, [cart, weightCart, itemsById]);

  // Mirror the server's bulk-discount math (same shared helper) so the buyer
  // sees the seller's discount applied here, not just baked invisibly into the
  // subtotal once the PaymentIntent returns.
  const bulkDiscount = useMemo(() => {
    const lines: CartDiscountLine[] = [];
    const push = (item: any, lineSubtotal: number) => {
      lines.push({
        sellerId: "s",
        lineSubtotal,
        discountThreshold:
          item?.bulkDiscountThresholdCents != null
            ? Number(item.bulkDiscountThresholdCents) / 100
            : null,
        discountPercent: item?.bulkDiscountPercent ?? null,
      });
    };
    cart.forEach((qty, itemId) => {
      const item = itemsById.get(itemId);
      if (item) push(item, parseFloat(item.price) * qty);
    });
    weightCart.forEach((line) => push(itemsById.get(line.itemId), line.priceCents / 100));
    return cartBulkDiscountTotal(lines);
  }, [cart, weightCart, itemsById]);

  const foodRiskLabelText = useMemo(
    () =>
      foodRiskLabel(
        [
          ...Array.from(cart.keys()),
          ...Array.from(weightCart.values()).map((line) => line.itemId),
        ].map((id) => itemsById.get(id)?.category),
      ),
    [cart, weightCart, itemsById],
  );
  const needsFoodRiskAck = foodRiskLabelText !== null;

  // Pre-intent we estimate the buyer fee + all-in total from the running
  // subtotal; once the PaymentIntent comes back its numbers are authoritative
  // (they match calculateFees exactly), so prefer them.
  const totals = useMemo(() => {
    if (intentTotals) return intentTotals;
    const net = Math.round((subtotal - bulkDiscount) * 100) / 100;
    const fees = calculateExpressFees({ subtotal: net });
    return { subtotal: net, buyerFee: fees.buyerFee, deliveryFee: 0, total: fees.buyerTotal };
  }, [intentTotals, subtotal, bulkDiscount]);

  const deliveryAddrComplete = Boolean(
    deliveryAddr.line1.trim() && deliveryAddr.city.trim() && deliveryAddr.state.trim() && deliveryAddr.zip.trim(),
  );

  // A new fulfillment choice or address invalidates any pending intent.
  useEffect(() => {
    setClientSecret("");
    setOrderId("");
    setIntentTotals(null);
  }, [fulfillment, deliveryAddr.line1, deliveryAddr.city, deliveryAddr.state, deliveryAddr.zip]);

  // Preview the delivery fee (debounced) once the address is complete, using
  // the same pricing the server charges.
  useEffect(() => {
    if (fulfillment !== "delivery" || !deliveryAddrComplete) {
      setDeliveryQuote(null);
      return;
    }
    let cancelled = false;
    setQuoting(true);
    const t = window.setTimeout(async () => {
      try {
        const qs = new URLSearchParams({
          handle,
          addressLine1: deliveryAddr.line1.trim(),
          city: deliveryAddr.city.trim(),
          state: deliveryAddr.state.trim(),
          zipCode: deliveryAddr.zip.trim(),
          subtotal: String(subtotal),
        });
        const res = await fetch(`/api/express/delivery-quote?${qs.toString()}`);
        const q: DeliveryQuote = await res.json();
        if (!cancelled) {
          setDeliveryQuote(q);
          trackEvent("express_delivery_quote", `/buy/${handle}`, {
            handle,
            available: q.available,
            free: q.free,
            estimatedMiles: q.estimatedMiles,
            reason: q.reason,
            subtotal,
            underMin: subtotal < EXPRESS_DELIVERY_MIN,
            zip: deliveryAddr.zip.trim(),
          });
        }
      } catch {
        if (!cancelled) setDeliveryQuote({ available: false, reason: "Couldn't estimate delivery" });
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [fulfillment, deliveryAddr.line1, deliveryAddr.city, deliveryAddr.state, deliveryAddr.zip, deliveryAddrComplete, handle, subtotal]);

  // Storefront delivery is complimentary within the service radius.
  const displayDeliveryFee = 0;
  const grandTotal = intentTotals ? intentTotals.total : totals.total + displayDeliveryFee;
  const deliveryUnderMin = fulfillment === "delivery" && subtotal < EXPRESS_DELIVERY_MIN;
  // The tiered platform fee this order would normally carry — shown struck
  // through so the founding-customer discount is visible.
  const usualFee = subtotal > 0 ? calculateFees({ subtotal }).buyerFee : 0;

  const setQty = (itemId: string, qty: number) => {
    const item = itemsById.get(itemId);
    const prevQty = cart.get(itemId) ?? 0;
    setCart((prev) => {
      const next = new Map(prev);
      if (qty <= 0) next.delete(itemId);
      else next.set(itemId, qty);
      return next;
    });
    setClientSecret("");
    setOrderId("");
    setIntentTotals(null);
    const meta = {
      cartSessionId: getCartSessionId(`express:${handle}`),
      handle,
      sellerId: seller?.id,
      itemId,
      name: item?.name,
      price: item ? parseFloat(item.price) : undefined,
      qty,
      prevQty,
      cartSubtotalBefore: subtotal,
    };
    if (qty > prevQty) trackCartAdd("express_cart_add", `/buy/${handle}`, meta);
    else trackEvent("express_cart_remove", `/buy/${handle}`, meta);
  };

  const addWeightUnit = (item: StorefrontItem, unit: ResolvedWeightUnit) => {
    setWeightCart((prev) => {
      const next = new Map(prev);
      next.set(unit.id, { itemId: item.id, weight: unit.weight, priceCents: unit.priceCents });
      return next;
    });
    setClientSecret("");
    setOrderId("");
    setIntentTotals(null);
    setDetailItem(null);
    trackCartAdd("express_cart_add", `/buy/${handle}`, {
      cartSessionId: getCartSessionId(`express:${handle}`),
      handle,
      sellerId: seller?.id,
      itemId: item.id,
      name: item.name,
      price: unit.priceCents / 100,
      weightUnitId: unit.id,
      weightCentiLb: unit.weight,
      cartSubtotalBefore: subtotal,
    });
  };

  const removeWeightUnit = (unitId: string) => {
    const line = weightCart.get(unitId);
    setWeightCart((prev) => {
      const next = new Map(prev);
      next.delete(unitId);
      return next;
    });
    setClientSecret("");
    setOrderId("");
    setIntentTotals(null);
    fetch("/api/cart/release-weight-unit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ unitId }),
    }).catch(() => {});
    trackEvent("express_cart_remove", `/buy/${handle}`, {
      handle,
      sellerId: seller?.id,
      itemId: line?.itemId,
      weightUnitId: unitId,
      cartSubtotalBefore: subtotal,
    });
  };

  const openDetail = (item: StorefrontItem) => {
    setDetailItem(item);
    trackEvent("express_item_detail_view", `/buy/${handle}`, {
      itemId: item.id,
      name: item.name,
      price: parseFloat(item.price),
      sellerId: seller?.id,
    });
  };

  const startCheckout = async () => {
    if (!email.trim()) {
      trackEvent("express_checkout_blocked", `/buy/${handle}`, { reason: "email_required", subtotal });
      toast({
        title: "Email required",
        description: "Enter your email so the seller can reach you about your order.",
        variant: "destructive",
      });
      return;
    }
    if (needsFoodRiskAck && !foodRiskAck) {
      toast({
        title: "One quick thing",
        description: "Please acknowledge the food-safety notice to continue.",
        variant: "destructive",
      });
      return;
    }
    // Express has no second screen to come back to, so the answers have to be
    // right here before we take money for something the seller can't make.
    const unanswered = Array.from(cart.keys())
      .map((id) => items.find((it) => it.id === id))
      .find(
        (it) =>
          it &&
          !madeToOrderResponsesComplete(
            ((it as any).customFields ?? []) as MadeToOrderField[],
            customAnswers.get(it.id),
          ),
      );
    if (unanswered) {
      toast({
        title: "One quick thing",
        description: `Choose your options for "${unanswered.name}" first.`,
        variant: "destructive",
      });
      return;
    }
    if (fulfillment === "delivery") {
      if (subtotal < EXPRESS_DELIVERY_MIN) {
        trackEvent("express_checkout_blocked", `/buy/${handle}`, { reason: "delivery_min", subtotal, min: EXPRESS_DELIVERY_MIN });
        toast({ title: "Delivery minimum", description: `Add more to reach the $${EXPRESS_DELIVERY_MIN} delivery minimum, or choose pickup.`, variant: "destructive" });
        return;
      }
      if (!deliveryAddrComplete) {
        trackEvent("express_checkout_blocked", `/buy/${handle}`, { reason: "address_incomplete", subtotal });
        toast({ title: "Add your address", description: "We need your full delivery address.", variant: "destructive" });
        return;
      }
      if (deliveryQuote && deliveryQuote.available === false) {
        trackEvent("express_checkout_blocked", `/buy/${handle}`, { reason: "delivery_unavailable", subtotal, quoteReason: deliveryQuote.reason });
        toast({ title: "Delivery not available", description: deliveryQuote.reason || "Please choose pickup.", variant: "destructive" });
        return;
      }
    }

    setCreating(true);
    try {
      const body = {
        handle,
        cartSessionId: getCartSessionId(`express:${handle}`),
        items: [
          ...Array.from(cart.entries()).map(([itemId, qty]) => ({
            itemId,
            qty,
            customResponses: customAnswers.get(itemId) ?? [],
          })),
          ...Array.from(weightCart.entries()).map(([unitId, line]) => ({
            itemId: line.itemId,
            qty: 1,
            weightUnitId: unitId,
          })),
        ],
        email: email.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(needsFoodRiskAck ? { foodRiskAck: true } : {}),
        ...(fulfillment === "delivery"
          ? {
              deliveryMethod: "delivery",
              deliveryAddress: {
                addressLine1: deliveryAddr.line1.trim(),
                city: deliveryAddr.city.trim(),
                state: deliveryAddr.state.trim(),
                zipCode: deliveryAddr.zip.trim(),
              },
            }
          : {}),
      };
      const res = await apiRequest("POST", "/api/express/create-payment-intent", body);
      const intent: IntentResponse = await res.json();
      setClientSecret(intent.clientSecret);
      setOrderId(intent.orderId);
      setIntentTotals({
        subtotal: intent.subtotal,
        buyerFee: intent.buyerFee,
        deliveryFee: intent.deliveryFee ?? 0,
        total: intent.total,
      });
      trackEvent("express_checkout_intent", `/buy/${handle}`, {
        orderId: intent.orderId,
        method: fulfillment,
        subtotal: intent.subtotal,
        buyerFee: intent.buyerFee,
        deliveryFee: intent.deliveryFee ?? 0,
        total: intent.total,
        itemCount,
      });
    } catch (error: any) {
      trackEvent("express_checkout_intent_failed", `/buy/${handle}`, {
        method: fulfillment,
        subtotal,
        message: error?.message,
      });
      toast({
        title: "Couldn't start checkout",
        description: error?.message || "Something went wrong. Try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handlePaymentSuccess = () => {
    trackEvent("express_payment_succeeded", `/buy/${handle}`, {
      orderId,
      method: fulfillment,
      subtotal: intentTotals?.subtotal,
      total: intentTotals?.total,
    });
    rotateCartSessionId(`express:${handle}`);
    // Stripe injects iframes under document.body that survive SPA nav, so use
    // a full reload to the confirmation query param rather than wouter.
    window.location.replace(`/buy/${handle}?paid=1&orderId=${orderId}`);
  };

  useEffect(() => {
    if (view === "checkout" && cart.size === 0 && weightCart.size === 0 && !paid) {
      setView("browse");
    }
  }, [cart.size, weightCart.size, view, paid]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !seller) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <Card className="w-full max-w-md">
          <CardContent className="space-y-3 p-8 text-center">
            <Store className="mx-auto h-10 w-10 text-muted-foreground" />
            <h1 className="text-xl font-semibold">Storefront not found</h1>
            <p className="text-sm text-muted-foreground">
              We couldn't find a seller at this link. Double-check the QR code or ask the seller
              to share it again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sellerLocation = [seller.city, seller.state].filter(Boolean).join(", ");

  // CONFIRMATION
  if (paid) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-11 w-11 text-emerald-600" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold">You're all set!</h1>
              <p className="text-muted-foreground">
                Your order is confirmed. We've emailed you the details.
              </p>
            </div>

            <Card className="rounded-2xl">
              <CardContent className="space-y-2 p-6 text-center">
                <p className="text-sm text-muted-foreground">Order confirmed</p>
                {paidOrderId && (
                  <p className="font-mono text-xs text-muted-foreground">
                    #{paidOrderId.slice(0, 8).toUpperCase()}
                  </p>
                )}
                <p className="text-sm font-medium text-foreground">{seller.displayName}</p>
              </CardContent>
            </Card>

            {showAppCard && (
              <Card className="relative overflow-hidden rounded-2xl border-primary/30 bg-primary/5 text-left">
                <CardContent className="space-y-3 p-5">
                  <button
                    type="button"
                    onClick={() => setShowAppCard(false)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <p className="font-semibold">Want live delivery updates?</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Get the Stoop app to track your order in real time, message{" "}
                    {seller.displayName}, and find more local makers near you.
                  </p>
                  <Button asChild className="w-full rounded-full">
                    <a href="/install">Get the app</a>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  // CHECKOUT
  if (view === "checkout") {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-md px-5 py-6">
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setView("browse")}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to {seller.displayName}
            </button>
            <button
              type="button"
              onClick={() => setStorefrontDark((d) => !d)}
              className="rounded-full border p-2 text-foreground hover:bg-accent"
              aria-label="Toggle light or dark mode"
            >
              {storefrontDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>

          <h1 className="mb-1 text-2xl font-semibold">Checkout</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            No account needed. Pay now, pick up or get it delivered.
          </p>

          <Card className="mb-5 rounded-2xl">
            <CardContent className="space-y-3 p-5">
              {Array.from(cart.entries()).map(([itemId, qty]) => {
                const item = itemsById.get(itemId);
                if (!item) return null;
                return (
                  <div key={itemId} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setQty(itemId, qty - 1)}
                        className="flex h-6 w-6 items-center justify-center rounded-full border hover:bg-accent"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-[1.25rem] text-center font-medium">{qty}</span>
                      <button
                        type="button"
                        onClick={() => setQty(itemId, qty + 1)}
                        className="flex h-6 w-6 items-center justify-center rounded-full border hover:bg-accent"
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <span className="w-14 text-right font-medium">
                      ${(parseFloat(item.price) * qty).toFixed(2)}
                    </span>
                  </div>
                );
              })}
              {Array.from(weightCart.entries()).map(([unitId, line]) => {
                const item = itemsById.get(line.itemId);
                if (!item) return null;
                return (
                  <div key={unitId} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">
                      {item.name}
                      {line.weight ? (
                        <span className="text-muted-foreground"> · {formatWeight(line.weight)}</span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeWeightUnit(unitId)}
                      className="flex h-6 w-6 items-center justify-center rounded-full border hover:bg-accent"
                      aria-label={`Remove ${item.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <span className="w-14 text-right font-medium">
                      ${(line.priceCents / 100).toFixed(2)}
                    </span>
                  </div>
                );
              })}
              <Separator />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              {bulkDiscount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Bulk discount
                    {subtotal > 0 ? ` (${Math.round((bulkDiscount / subtotal) * 100)}%)` : ""}
                  </span>
                  <span className="font-medium text-emerald-700">
                    −${bulkDiscount.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setShowFeeInfo((v) => !v)}
                  className="flex items-center gap-1 text-muted-foreground"
                >
                  Service fee <Info className="h-3.5 w-3.5" />
                </button>
                <span className="font-medium">
                  {usualFee > totals.buyerFee && (
                    <span className="mr-1.5 text-xs font-normal text-muted-foreground line-through">
                      ${usualFee.toFixed(2)}
                    </span>
                  )}
                  ${totals.buyerFee.toFixed(2)}
                </span>
              </div>
              {showFeeInfo && (
                <p className="rounded-lg bg-muted/50 p-2 text-xs text-muted-foreground">
                  This just covers card processing. As a founding customer, your service fee is
                  reduced from the usual{usualFee > totals.buyerFee ? ` $${usualFee.toFixed(2)}` : ""}, our way
                  of saying thanks for shopping local early. 🎉
                </p>
              )}
              {fulfillment === "delivery" && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Delivery</span>
                  <span className="font-medium">
                    {quoting
                      ? "…"
                      : deliveryQuote && deliveryQuote.available === false
                        ? "—"
                        : (intentTotals?.deliveryFee ?? displayDeliveryFee) === 0
                          ? "Free"
                          : `$${(intentTotals?.deliveryFee ?? displayDeliveryFee).toFixed(2)}`}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="text-base font-bold">${grandTotal.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          {!clientSecret ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setFulfillment("pickup"); trackEvent("express_method_selected", `/buy/${handle}`, { method: "pickup", subtotal }); }}
                  className={`rounded-full border px-3 py-2 text-sm font-medium ${fulfillment === "pickup" ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"}`}
                >
                  Pickup
                </button>
                <button
                  type="button"
                  onClick={() => { setFulfillment("delivery"); trackEvent("express_method_selected", `/buy/${handle}`, { method: "delivery", subtotal, underMin: subtotal < EXPRESS_DELIVERY_MIN }); }}
                  className={`rounded-full border px-3 py-2 text-sm font-medium ${fulfillment === "delivery" ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"}`}
                >
                  Delivery
                </button>
              </div>

              {fulfillment === "delivery" && (
                <div className="space-y-2 rounded-xl border p-3">
                  {deliveryUnderMin && (
                    <p className="text-xs font-medium text-amber-700">
                      Add ${(EXPRESS_DELIVERY_MIN - subtotal).toFixed(2)} more to your cart to hit the ${EXPRESS_DELIVERY_MIN} delivery minimum, and unlock free delivery on your first order.
                    </p>
                  )}
                  <Input
                    placeholder="Street address"
                    autoComplete="address-line1"
                    value={deliveryAddr.line1}
                    onChange={(e) => setDeliveryAddr((a) => ({ ...a, line1: e.target.value }))}
                  />
                  <div className="grid grid-cols-6 gap-2">
                    <Input
                      className="col-span-3"
                      placeholder="City"
                      autoComplete="address-level2"
                      value={deliveryAddr.city}
                      onChange={(e) => setDeliveryAddr((a) => ({ ...a, city: e.target.value }))}
                    />
                    <Input
                      className="col-span-1"
                      placeholder="ST"
                      maxLength={2}
                      value={deliveryAddr.state}
                      onChange={(e) => setDeliveryAddr((a) => ({ ...a, state: e.target.value.toUpperCase() }))}
                    />
                    <Input
                      className="col-span-2"
                      placeholder="ZIP"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      value={deliveryAddr.zip}
                      onChange={(e) => setDeliveryAddr((a) => ({ ...a, zip: e.target.value }))}
                    />
                  </div>
                  {deliveryAddrComplete && (
                    <p className="text-xs text-muted-foreground">
                      {quoting
                        ? "Checking delivery…"
                        : deliveryQuote?.available === false
                          ? `${deliveryQuote.reason || "Delivery isn't available to that address."} Choose pickup instead.`
                          : deliveryQuote?.available
                            ? `Free delivery to your door 🎉 (~${deliveryQuote.estimatedMiles} mi)`
                            : ""}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="express-email">
                  Email
                </label>
                <Input
                  id="express-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="input-express-email"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="express-name">
                  First name <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <Input
                  id="express-name"
                  type="text"
                  autoComplete="given-name"
                  placeholder="Alex"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="input-express-name"
                />
              </div>
              {needsFoodRiskAck && (
                <FoodRiskAck
                  label={foodRiskLabelText!}
                  checked={foodRiskAck}
                  onChange={setFoodRiskAck}
                />
              )}
              <Button
                size="lg"
                className="w-full rounded-full"
                onClick={startCheckout}
                disabled={creating || deliveryUnderMin}
                data-testid="button-express-continue"
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading payment...
                  </>
                ) : (
                  "Continue to payment"
                )}
              </Button>
            </div>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm
                handle={handle}
                orderId={orderId}
                total={totals.total}
                onSuccess={handlePaymentSuccess}
              />
            </Elements>
          )}
        </div>
      </div>
    );
  }

  // BROWSE
  return (
    <div className="min-h-screen bg-background pb-28" style={{ paddingTop: "var(--safe-area-top)" }}>
      {showInstallBar && (
        <div className="flex items-center justify-between gap-2 bg-primary/10 px-4 py-2 text-xs">
          <span className="text-primary">Shop right here, no app needed.</span>
          <span className="flex items-center gap-2">
            <a href="/install" className="font-semibold text-primary underline">
              Get the app
            </a>
            <button type="button" onClick={() => setShowInstallBar(false)} aria-label="Dismiss">
              <X className="h-3.5 w-3.5 text-primary/70" />
            </button>
          </span>
        </div>
      )}
      <div className="relative h-40 w-full bg-muted sm:h-52">
        <button
          type="button"
          onClick={() => setStorefrontDark((d) => !d)}
          className="absolute right-3 top-3 z-20 rounded-full bg-background/80 p-2 text-foreground shadow-sm backdrop-blur hover:bg-background"
          aria-label="Toggle light or dark mode"
        >
          {storefrontDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        {seller.coverImageUrl && (
          <img
            src={seller.coverImageUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        {/* Avatar lives inside the cover and is explicitly on top (z-10) so it
            overlaps the cover's bottom edge instead of hiding behind it. */}
        <div className="absolute -bottom-8 left-5 z-10 h-20 w-20 overflow-hidden rounded-2xl border-4 border-background bg-muted shadow-md">
          {seller.profileImageUrl ? (
            <img
              src={seller.profileImageUrl}
              alt={seller.displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center bg-muted text-2xl font-semibold text-muted-foreground"
              aria-label={seller.displayName}
            >
              {String(seller.displayName || "?").trim().charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-lg px-5 pt-12">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold leading-tight">{seller.displayName}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {seller.isFoundingMaker && <FoundingMakerBadge />}
            {seller.sellerType && (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium capitalize text-primary">
                {seller.sellerType}
              </span>
            )}
            {sellerLocation && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                {sellerLocation}
              </span>
            )}
          </div>
        </div>

        {seller.bio && (
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{seller.bio}</p>
        )}

        {!seller.chargesEnabled && (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {seller.displayName} isn't taking orders here just yet. Check back soon!
          </div>
        )}

        <div className="mb-3 mt-6 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Available now
          </h2>
          {items.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {items.length} {items.length === 1 ? "item" : "items"}
            </span>
          )}
        </div>

        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
            No products available right now.
          </div>
        ) : (
          <div className="grid grid-cols-2 items-start gap-3">
            {items.map((item) => {
              const qty = cart.get(item.id) ?? 0;
              const soldByWeight = Boolean((item as any).soldByWeight);
              const wMin = (item as any).weightMin as number | null | undefined;
              const wMax = (item as any).weightMax as number | null | undefined;
              const tier = (item as any).sizeTier as string | null | undefined;
              const unitsAvailable = ((item as any).unitsAvailable as number | null | undefined) ?? 0;
              const soldOut = !soldByWeight && Number((item as any).qtyAvailable ?? 0) <= 0;
              const rangeLabel =
                soldByWeight && wMin != null && wMax != null
                  ? wMin === wMax
                    ? formatWeight(wMin)
                    : `${formatWeight(wMin)}–${formatWeight(wMax)}`
                  : null;
              return (
                <Card key={item.id} className="flex flex-col overflow-hidden rounded-2xl">
                  <div
                    className="relative aspect-square w-full shrink-0 cursor-pointer overflow-hidden bg-muted"
                    onClick={() => openDetail(item)}
                  >
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    ) : (
                      <ListingPhotoPlaceholder category={(item as any).category} size="sm" />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-3">
                    <p
                      className="min-h-[2.75rem] cursor-pointer font-semibold leading-snug line-clamp-2"
                      onClick={() => openDetail(item)}
                    >
                      {item.name}
                    </p>
                    {item.heatLevel != null && (
                      <span className="mt-1">
                        <HeatBadge level={item.heatLevel} />
                      </span>
                    )}
                    <p className="mt-auto pt-2 font-semibold">
                      {soldByWeight && (
                        <span className="text-xs font-normal text-muted-foreground">from </span>
                      )}
                      ${parseFloat(item.price).toFixed(2)}
                      {soldByWeight ? (
                        rangeLabel && (
                          <span className="text-xs font-normal text-muted-foreground">
                            {" "}
                            · {(tier ? `${tier} · ` : "") + rangeLabel}
                          </span>
                        )
                      ) : (
                        item.unit && (
                          <span className="text-xs font-normal text-muted-foreground">
                            /{item.unit}
                          </span>
                        )
                      )}
                    </p>
                    {seller.chargesEnabled && soldByWeight ? (
                      <div className="mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full rounded-full"
                          onClick={() => setSizePickerItem(item)}
                          data-testid={`button-choose-size-${item.id}`}
                        >
                          {unitsAvailable <= 0 ? "Sold out" : "Choose size"}
                        </Button>
                      </div>
                    ) : seller.chargesEnabled && soldOut ? (
                      <div className="mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full rounded-full"
                          disabled
                          data-testid={`button-sold-out-${item.id}`}
                        >
                          Sold out
                        </Button>
                      </div>
                    ) : seller.chargesEnabled && (
                      <div className="mt-2">
                        {qty === 0 ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full rounded-full"
                            onClick={() => setQty(item.id, 1)}
                            data-testid={`button-add-${item.id}`}
                          >
                            <Plus className="mr-1 h-4 w-4" />
                            Add
                          </Button>
                        ) : (
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => setQty(item.id, qty - 1)}
                              className="flex h-8 w-8 items-center justify-center rounded-full border hover:bg-accent"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="min-w-[1.5rem] text-center text-sm font-semibold">
                              {qty}
                            </span>
                            <button
                              type="button"
                              onClick={() => setQty(item.id, qty + 1)}
                              className="flex h-8 w-8 items-center justify-center rounded-full border hover:bg-accent"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                        {qty > 0 && (((item as any).customFields ?? []) as MadeToOrderField[]).length > 0 && (
                          <div className="mt-3 space-y-2 border-t pt-3 text-left">
                            <MadeToOrderFields
                              fields={((item as any).customFields ?? []) as MadeToOrderField[]}
                              responses={customAnswers.get(item.id) ?? []}
                              onChange={(next) =>
                                setCustomAnswers((cur) => new Map(cur).set(item.id, next))
                              }
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {detailItem && (() => {
          const d = detailItem;
          const dWeight = Boolean((d as any).soldByWeight);
          const dSoldOut = !dWeight && Number((d as any).qtyAvailable ?? 0) <= 0;
          const dUnits = ((d as any).unitsAvailable as number | null | undefined) ?? 0;
          const dQty = cart.get(d.id) ?? 0;
          return (
            <div
              className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
              onClick={() => setDetailItem(null)}
            >
              <div
                className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-background sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative aspect-square w-full bg-muted">
                  {d.imageUrl ? (
                    <img src={d.imageUrl} alt={d.name} className="h-full w-full object-cover" />
                  ) : (
                    <ListingPhotoPlaceholder category={(d as any).category} />
                  )}
                  <button
                    type="button"
                    onClick={() => setDetailItem(null)}
                    aria-label="Close"
                    className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-xl leading-none text-white"
                  >
                    ×
                  </button>
                </div>
                <div className="p-4">
                  <p className="text-lg font-semibold leading-snug">{d.name}</p>
                  {d.heatLevel != null && (
                    <span className="mt-1 inline-block">
                      <HeatBadge level={d.heatLevel} />
                    </span>
                  )}
                  <p className="mt-2 font-semibold">
                    ${parseFloat(d.price).toFixed(2)}
                    {d.unit && <span className="text-sm font-normal text-muted-foreground">/{d.unit}</span>}
                  </p>
                  {d.description && (
                    <p className="mt-3 whitespace-pre-line text-sm text-muted-foreground">{d.description}</p>
                  )}
                  {seller.chargesEnabled && (
                    <div className="mt-4">
                      {dWeight ? (
                        <Button
                          className="w-full rounded-full"
                          variant="outline"
                          onClick={() => setSizePickerItem(d)}
                        >
                          {dUnits <= 0 ? "Sold out" : "Choose size"}
                        </Button>
                      ) : dSoldOut ? (
                        <Button className="w-full rounded-full" variant="outline" disabled>
                          Sold out
                        </Button>
                      ) : dQty === 0 ? (
                        <Button className="w-full rounded-full" onClick={() => setQty(d.id, 1)}>
                          <Plus className="mr-1 h-4 w-4" />
                          Add to cart
                        </Button>
                      ) : (
                        <div className="flex items-center justify-between rounded-full border px-2 py-1">
                          <button type="button" onClick={() => setQty(d.id, dQty - 1)} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-accent" aria-label="Decrease quantity">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="min-w-[2rem] text-center font-semibold">{dQty}</span>
                          <button type="button" onClick={() => setQty(d.id, dQty + 1)} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-accent" aria-label="Increase quantity">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {sizePickerItem && (
          <WeightSizePicker
            open
            onOpenChange={(open) => {
              if (!open) setSizePickerItem(null);
            }}
            itemId={sizePickerItem.id}
            tierLabel={(sizePickerItem as any).sizeTier ?? null}
            itemName={sizePickerItem.name}
            onSelected={(unit) => addWeightUnit(sizePickerItem, unit)}
          />
        )}

        <footer className="mt-10 border-t pt-6 text-center text-xs text-muted-foreground">
          Powered by Stoop{seller.city ? ` · Shop local in ${seller.city}` : ""}
        </footer>
      </div>

      {seller.chargesEnabled && itemCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 border-t bg-background/95 backdrop-blur">
          <div className="mx-auto max-w-lg px-5 py-3">
            <div className="space-y-1 pb-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Service fee</span>
                <span className="font-medium">${totals.buyerFee.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="font-bold">${totals.total.toFixed(2)}</span>
              </div>
            </div>
            {totals.subtotal < EXPRESS_MIN_ORDER && (
              <p className="pb-2 text-xs font-medium text-amber-700">
                Add ${(EXPRESS_MIN_ORDER - totals.subtotal).toFixed(2)} more to check out (${EXPRESS_MIN_ORDER} minimum).
              </p>
            )}
            <div className="flex items-center gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="relative">
                  <ShoppingBag className="h-6 w-6 text-foreground" />
                  <span className="absolute -right-2 -top-2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
                    {itemCount}
                  </span>
                </div>
                <span className="text-lg font-semibold">${totals.total.toFixed(2)}</span>
              </div>
              <Button
                size="lg"
                className="rounded-full"
                onClick={() => {
                  setView("checkout");
                  trackEvent("express_checkout_opened", `/buy/${handle}`, {
                    itemCount,
                    subtotal,
                    estBuyerFee: totals.buyerFee,
                    estTotal: totals.total,
                    items: [
                      ...Array.from(cart.entries()).map(([id, q]) => ({
                        id,
                        name: itemsById.get(id)?.name,
                        price: itemsById.get(id) ? parseFloat(itemsById.get(id)!.price) : undefined,
                        qty: q,
                      })),
                      ...Array.from(weightCart.entries()).map(([unitId, line]) => ({
                        id: line.itemId,
                        name: itemsById.get(line.itemId)?.name,
                        price: line.priceCents / 100,
                        qty: 1,
                        weightUnitId: unitId,
                      })),
                    ],
                  });
                }}
                disabled={totals.subtotal < EXPRESS_MIN_ORDER}
                data-testid="button-express-checkout"
              >
                Checkout
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
